import { Injectable, OnDestroy, inject } from '@angular/core';
import { AngularFirestore } from '@newgenus/fire-compat/firestore';
import { AngularFireAuth } from '@newgenus/fire-compat/auth';
import { Observable, ReplaySubject, Subject, firstValueFrom } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, takeUntil } from 'rxjs/operators';
import { Timestamp } from 'firebase/firestore';
import { OrgDraft, OrgDraftStep, ORG_DRAFT_MAX_IN_PROGRESS_PER_USER, ORG_DRAFT_RETENTION_DAYS } from '@newgenus/common';

/**
 * Result of attempting to start a brand-new draft.
 *
 * `cap-reached` means the user already holds {@link ORG_DRAFT_MAX_IN_PROGRESS_PER_USER}
 * in-progress drafts (D11). The wizard then surfaces the drafts-picker dialog.
 */
export type StartDraftResult = { kind: 'created'; draft: OrgDraft } | { kind: 'cap-reached'; drafts: OrgDraft[] };

/**
 * Firestore-backed lifecycle for `orgDrafts/{draftKey}` documents.
 *
 * Responsibilities (P1 scope):
 * - CRUD on a draft (create / get / list / patch / abandon).
 * - Enforce the D11 per-user 3-in-progress cap (client-side; the
 *   `createFromDraft` callable enforces it again server-side).
 * - Compute and write `expiresOn` on create (D10).
 *
 * Autosave debouncing lives in {@link OrgWizardStateService}; this service
 * exposes a thin `patch` method that performs a single Firestore write.
 *
 * Plan reference: docs/features/CREATE-ORGANIZATION-WIZARD-PLAN.md §5.1
 */
@Injectable({ providedIn: 'root' })
export class OrgDraftService implements OnDestroy {
    private db = inject(AngularFirestore);
    private auth = inject(AngularFireAuth);

    private destroy$ = new Subject<void>();
    private collectionPath = 'orgDrafts';

    /**
     * Emits the current user's list of `in-progress` drafts. Replays the last value
     * so late subscribers (header dropdown, drafts picker dialog) get the cached snapshot.
     */
    public readonly myInProgressDrafts$: Observable<OrgDraft[]> = this.auth.user.pipe(
        filter((u): u is NonNullable<typeof u> => !!u),
        map((u) => u.uid),
        distinctUntilChanged(),
        switchMap((uid) =>
            this.db
                .collection<OrgDraft>(this.collectionPath, (ref) =>
                    ref.where('createdBy', '==', uid).where('status', '==', 'in-progress')
                )
                .valueChanges({ idField: 'key' })
        ),
        takeUntil(this.destroy$)
    );

    private readonly currentDraft$$ = new ReplaySubject<OrgDraft | null>(1);
    public readonly currentDraft$ = this.currentDraft$$.asObservable();

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    // ---------------------------------------------------------------------
    // Read
    // ---------------------------------------------------------------------

    /**
     * One-shot fetch of a specific draft. Returns `null` if the draft does not
     * exist or the caller does not own it (rules will block the read).
     */
    public async get(draftKey: string): Promise<OrgDraft | null> {
        const snap = await firstValueFrom(this.db.collection<OrgDraft>(this.collectionPath).doc(draftKey).get());
        if (!snap.exists) return null;
        const data = snap.data() as OrgDraft;
        return { ...data, key: draftKey };
    }

    /**
     * Snapshot listener that pushes into {@link currentDraft$}. Caller is responsible
     * for calling {@link stopWatchingCurrent} when navigating away.
     */
    public watchCurrent(draftKey: string): void {
        this.db
            .collection<OrgDraft>(this.collectionPath)
            .doc(draftKey)
            .valueChanges({ idField: 'key' })
            .pipe(takeUntil(this.destroy$))
            .subscribe((draft) => this.currentDraft$$.next(draft ?? null));
    }

    public stopWatchingCurrent(): void {
        this.currentDraft$$.next(null);
    }

    /** One-shot count of the user's `in-progress` drafts (used by the cap check). */
    public async countInProgressForCurrentUser(): Promise<number> {
        const user = await this.auth.currentUser;
        if (!user) return 0;
        const snap = await firstValueFrom(
            this.db
                .collection<OrgDraft>(this.collectionPath, (ref) =>
                    ref.where('createdBy', '==', user.uid).where('status', '==', 'in-progress')
                )
                .get()
        );
        return snap.size;
    }

    // ---------------------------------------------------------------------
    // Write
    // ---------------------------------------------------------------------

    /**
     * Create a brand-new draft for the signed-in user, enforcing the D11 cap.
     *
     * - If the user already has {@link ORG_DRAFT_MAX_IN_PROGRESS_PER_USER} in-progress
     *   drafts, returns `cap-reached` with the list so the wizard can show the picker.
     * - Otherwise writes the draft doc and returns it.
     */
    public async startDraft(): Promise<StartDraftResult> {
        const user = await this.auth.currentUser;
        if (!user) throw new Error('Must be signed in to create an organization draft.');

        // Cap check (client-side; backend re-checks).
        const existingSnap = await firstValueFrom(
            this.db
                .collection<OrgDraft>(this.collectionPath, (ref) =>
                    ref.where('createdBy', '==', user.uid).where('status', '==', 'in-progress')
                )
                .get()
        );
        if (existingSnap.size >= ORG_DRAFT_MAX_IN_PROGRESS_PER_USER) {
            const drafts = existingSnap.docs.map((d) => ({ ...(d.data() as OrgDraft), key: d.id }));
            return { kind: 'cap-reached', drafts };
        }

        const key = this.db.createId();
        const now = Timestamp.now();
        const expiresOn = Timestamp.fromMillis(now.toMillis() + ORG_DRAFT_RETENTION_DAYS * 24 * 60 * 60 * 1000);

        const draft: OrgDraft = {
            key,
            createdBy: user.uid,
            createdOn: now,
            modifiedBy: user.uid,
            modifiedOn: now,
            status: 'in-progress',
            currentStep: 1,
            expiresOn,
            sourceMode: 'blank',
            basics: { name: '', description: '', settings: {} },
            structure: [],
            staff: [],
            features: [],
        };

        await this.db.collection(this.collectionPath).doc(key).set(draft);
        return { kind: 'created', draft };
    }

    /**
     * Apply a partial update to an existing draft. Always stamps
     * `modifiedOn` + `modifiedBy`.
     */
    public async patch(draftKey: string, patch: Partial<OrgDraft>): Promise<void> {
        const user = await this.auth.currentUser;
        if (!user) throw new Error('Must be signed in to update a draft.');
        const stamped: Partial<OrgDraft> = {
            ...patch,
            modifiedBy: user.uid,
            modifiedOn: Timestamp.now(),
        };
        await this.db.collection(this.collectionPath).doc(draftKey).update(stamped);
    }

    /** Update only the step pointer — separated to keep autosave traces tight. */
    public async setStep(draftKey: string, step: OrgDraftStep): Promise<void> {
        return this.patch(draftKey, { currentStep: step });
    }

    /** Mark a draft as abandoned. Daily prune deletes shortly after. */
    public async abandon(draftKey: string): Promise<void> {
        return this.patch(draftKey, { status: 'abandoned' });
    }

    /**
     * Permanently delete a draft (used by the drafts-picker "trash this" action when
     * the user wants to free a slot immediately).
     */
    public async hardDelete(draftKey: string): Promise<void> {
        await this.db.collection(this.collectionPath).doc(draftKey).delete();
    }
}
